/* eslint-disable no-unused-vars */
import { db, storage } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const DOCUMENT_TYPES = {
  CERTIFICATE_OF_INSURANCE: 'certificate_of_insurance',
  FOOD_SAFE_PERMIT: 'food_safe_permit',
  HEALTH_PERMIT: 'health_permit',
  BUSINESS_LICENSE: 'business_license',
  SELLERS_PERMIT: 'sellers_permit',
  LIABILITY_INSURANCE: 'liability_insurance',
  WORKERS_COMP: 'workers_compensation',
  OTHER: 'other'
};

const DOCUMENT_TYPE_LABELS = {
  certificate_of_insurance: 'Certificate of Insurance',
  food_safe_permit: 'Food Safe Permit',
  health_permit: 'Health Permit',
  business_license: 'Business License',
  sellers_permit: "Seller's Permit",
  liability_insurance: 'Liability Insurance',
  workers_compensation: "Workers' Compensation",
  other: 'Other Document'
};

const getDocumentStatus = (expiresAt) => {
  if (!expiresAt) return 'valid';
  
  const now = new Date();
  const expiryDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
  const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 30) return 'expiring_soon';
  return 'valid';
};

export const uploadVendorDocument = async (vendorId, file, documentData) => {
  try {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storagePath = `vendor-documents/${vendorId}/${fileName}`;
    
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    const docRef = await addDoc(collection(db, 'vendors', vendorId, 'documents'), {
      name: documentData.name || file.name,
      type: documentData.type || DOCUMENT_TYPES.OTHER,
      description: documentData.description || '',
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      storagePath,
      downloadURL,
      expiresAt: documentData.expiresAt || null,
      status: documentData.expiresAt ? getDocumentStatus(documentData.expiresAt) : 'valid',
      uploadedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { 
      success: true, 
      documentId: docRef.id,
      message: 'Document uploaded successfully' 
    };
  } catch (error) {
    console.error('Error uploading vendor document:', error);
    return { success: false, error: error.message };
  }
};

export const getVendorDocuments = async (vendorId) => {
  try {
    const docsRef = collection(db, 'vendors', vendorId, 'documents');
    const q = query(docsRef, orderBy('uploadedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const documents = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        status: data.expiresAt ? getDocumentStatus(data.expiresAt) : 'valid'
      };
    });
    
    return { success: true, documents };
  } catch (error) {
    console.error('Error fetching vendor documents:', error);
    return { success: false, documents: [], error: error.message };
  }
};

export const getVendorDocument = async (vendorId, documentId) => {
  try {
    const docRef = doc(db, 'vendors', vendorId, 'documents', documentId);
    const snapshot = await getDoc(docRef);
    
    if (!snapshot.exists()) {
      return { success: false, error: 'Document not found' };
    }
    
    const data = snapshot.data();
    return { 
      success: true, 
      document: {
        id: snapshot.id,
        ...data,
        status: data.expiresAt ? getDocumentStatus(data.expiresAt) : 'valid'
      }
    };
  } catch (error) {
    console.error('Error fetching vendor document:', error);
    return { success: false, error: error.message };
  }
};

export const updateVendorDocument = async (vendorId, documentId, updates) => {
  try {
    const docRef = doc(db, 'vendors', vendorId, 'documents', documentId);
    
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    if (updates.expiresAt) {
      updateData.status = getDocumentStatus(updates.expiresAt);
    }
    
    await updateDoc(docRef, updateData);
    
    return { success: true, message: 'Document updated successfully' };
  } catch (error) {
    console.error('Error updating vendor document:', error);
    return { success: false, error: error.message };
  }
};

export const deleteVendorDocument = async (vendorId, documentId) => {
  try {
    const docRef = doc(db, 'vendors', vendorId, 'documents', documentId);
    const snapshot = await getDoc(docRef);
    
    if (!snapshot.exists()) {
      return { success: false, error: 'Document not found' };
    }
    
    const data = snapshot.data();
    
    if (data.storagePath) {
      try {
        const storageRef = ref(storage, data.storagePath);
        await deleteObject(storageRef);
      } catch (storageError) {
        console.warn('Could not delete file from storage:', storageError);
      }
    }
    
    await deleteDoc(docRef);
    
    return { success: true, message: 'Document deleted successfully' };
  } catch (error) {
    console.error('Error deleting vendor document:', error);
    return { success: false, error: error.message };
  }
};

export const getVendorDocumentsForEvent = async (vendorId) => {
  try {
    const result = await getVendorDocuments(vendorId);
    if (!result.success) return result;
    
    const validDocuments = result.documents.filter(doc => doc.status !== 'expired');
    
    return { 
      success: true, 
      documents: validDocuments,
      hasExpiredDocuments: result.documents.some(doc => doc.status === 'expired'),
      hasExpiringDocuments: result.documents.some(doc => doc.status === 'expiring_soon')
    };
  } catch (error) {
    console.error('Error fetching vendor documents for event:', error);
    return { success: false, documents: [], error: error.message };
  }
};

export const getDocumentComplianceStatus = async (vendorId) => {
  try {
    const result = await getVendorDocuments(vendorId);
    if (!result.success) return { ...result, isCompliant: false };
    
    const stats = {
      total: result.documents.length,
      valid: result.documents.filter(d => d.status === 'valid').length,
      expiringSoon: result.documents.filter(d => d.status === 'expiring_soon').length,
      expired: result.documents.filter(d => d.status === 'expired').length
    };
    
    return {
      success: true,
      stats,
      isCompliant: stats.expired === 0,
      hasWarnings: stats.expiringSoon > 0,
      documents: result.documents
    };
  } catch (error) {
    console.error('Error checking document compliance:', error);
    return { success: false, isCompliant: false, error: error.message };
  }
};

export { DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS, getDocumentStatus };
